import { useEffect, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
// @mui
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
//
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useParams, useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

// ----------------------------------------------------------------------

const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 20;

type Props = {
  mode: 'success' | 'cancel';
};

export default function PaymentReturnPage({ mode }: Props) {
  const params = useParams();
  const router = useRouter();
  const filingId = params.id as string;
  const [timedOut, setTimedOut] = useState(false);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (mode === 'cancel') {
      enqueueSnackbar('Payment canceled. Your filing is saved', { variant: 'info' });
      router.replace(paths.filings.wizard(filingId));
      return undefined;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      pollsRef.current += 1;
      try {
        const response = await axios.post(
          formatEndpoint(endpoints.filings.verifyPayment, { id: filingId })
        );
        if (response.data.paymentStatus === 'paid') {
          router.replace(paths.filings.report(filingId));
          return;
        }
      } catch (ignored) {
        // keep polling: transient errors are expected right after redirect
      }
      if (pollsRef.current >= MAX_POLLS) {
        setTimedOut(true);
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
    };
  }, [filingId, mode, router]);

  if (timedOut) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 10 }}>
        <Typography variant="h6">Payment received? This can take a minute.</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          We haven&apos;t seen the payment confirmation yet. It usually arrives within a few seconds.
        </Typography>
        <Button variant="contained" color="secondary" onClick={() => window.location.reload()}>
          Check again
        </Button>
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" spacing={2} sx={{ py: 10 }} data-testid="payment-confirming">
      <CircularProgress />
      <Typography variant="body1">Confirming your payment…</Typography>
    </Stack>
  );
}
